import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAcList, type AcEvent } from "../api/accessControlApi";
import {
  ConsolePage,
  FilterSelect,
  KpiRow,
  KpiTile,
  LoadState,
  METHOD_ICON,
  Panel,
  RESULT_TONE,
  SearchBox,
  StatusChip,
  ToolButton,
  formatDateTime,
  toneOf,
} from "../components/console/Console";

// ===========================================================================
// Control de acceso → Bitácora de eventos: búsqueda de eventos por periodo,
// sitio, punto, resultado, método y tipo de sujeto, con exportación a CSV.
// ===========================================================================

const RANGES = { "24h": 24, "72h": 72, "7d": 168 } as const;
const METHODS = ["card", "face", "qr", "plate", "pin", "fingerprint", "manual"] as const;
const PAGE = 50;

export function eventsToCsv(rows: AcEvent[]): string {
  const head = ["occurred_at", "site", "point", "subject_type", "subject", "plate", "method", "result", "direction", "reason"];
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [head.join(","), ...rows.map((e) => [e.occurred_at, e.site_name, e.point_name, e.subject_type, e.subject_name, e.plate, e.method, e.result, e.direction, e.reason].map(esc).join(","))].join("\n");
}

export default function EventsLog() {
  const { t, i18n } = useTranslation("accessConsole");
  const [range, setRange] = useState<keyof typeof RANGES>("72h");
  const since = useMemo(() => new Date(Date.now() - RANGES[range] * 3600_000).toISOString(), [range]);
  const eventsQ = useAcList("events", { since }, { refetchInterval: 30_000 });
  const sitesQ = useAcList("sites");
  const pointsQ = useAcList("points");

  const [search, setSearch] = useState("");
  const [site, setSite] = useState("");
  const [point, setPoint] = useState("");
  const [result, setResult] = useState("");
  const [method, setMethod] = useState("");
  const [subject, setSubject] = useState("");
  const [page, setPage] = useState(0);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (eventsQ.data ?? []).filter(
      (e) =>
        (!site || e.site_id === site) &&
        (!point || e.point_id === point) &&
        (!result || e.result === result) &&
        (!method || e.method === method) &&
        (!subject || e.subject_type === subject) &&
        (!q || `${e.subject_name} ${e.plate ?? ""} ${e.point_name ?? ""} ${e.reason ?? ""}`.toLowerCase().includes(q)),
    );
  }, [eventsQ.data, search, site, point, result, method, subject]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const visible = rows.slice(page * PAGE, page * PAGE + PAGE);

  const exportCsv = () => {
    const blob = new Blob([`﻿${eventsToCsv(rows)}`], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `bitacora-accesos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const reset = () => setPage(0);

  return (
    <ConsolePage icon="ri-file-list-3-line" title={t("events.title")} subtitle={t("events.subtitle")} actions={<ToolButton icon="ri-download-2-line" onClick={exportCsv} disabled={!rows.length}>{t("events.export")}</ToolButton>}>
      <KpiRow>
        <KpiTile icon="ri-list-check-2" label={t("events.total")} value={rows.length} />
        <KpiTile icon="ri-shield-check-line" tone="success" label={t("result.granted")} value={rows.filter((e) => e.result === "granted").length} />
        <KpiTile icon="ri-shield-cross-line" tone="error" label={t("result.denied")} value={rows.filter((e) => e.result === "denied").length} />
        <KpiTile icon="ri-user-line" tone="info" label={t("subject.person")} value={rows.filter((e) => e.subject_type === "person").length} />
        <KpiTile icon="ri-car-line" tone="neutral" label={t("subject.vehicle")} value={rows.filter((e) => e.subject_type === "vehicle").length} />
        <KpiTile icon="ri-qr-code-line" tone="primary" label={t("method.qr")} value={rows.filter((e) => e.method === "qr").length} />
      </KpiRow>

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox value={search} onChange={(v) => { setSearch(v); reset(); }} placeholder={t("events.searchPlaceholder")} />
        <FilterSelect label={t("events.range")} value={range} onChange={(v) => { setRange(v as keyof typeof RANGES); reset(); }} options={Object.keys(RANGES).map((k) => ({ value: k, label: t(`events.ranges.${k}`) }))} />
        <FilterSelect label={t("fields.site")} value={site} onChange={(v) => { setSite(v); setPoint(""); reset(); }} options={[{ value: "", label: t("common.allSites") }, ...(sitesQ.data ?? []).map((s) => ({ value: s.id, label: s.name }))]} />
        <FilterSelect label={t("fields.point")} value={point} onChange={(v) => { setPoint(v); reset(); }} options={[{ value: "", label: t("events.allPoints") }, ...(pointsQ.data ?? []).filter((p) => !site || p.site_id === site).map((p) => ({ value: p.id, label: p.name }))]} />
        <FilterSelect label={t("fields.result")} value={result} onChange={(v) => { setResult(v); reset(); }} options={[{ value: "", label: t("events.allResults") }, { value: "granted", label: t("result.granted") }, { value: "denied", label: t("result.denied") }]} />
        <FilterSelect label={t("fields.method")} value={method} onChange={(v) => { setMethod(v); reset(); }} options={[{ value: "", label: t("events.allMethods") }, ...METHODS.map((m) => ({ value: m, label: t(`method.${m}`) }))]} />
        <FilterSelect label={t("fields.subject")} value={subject} onChange={(v) => { setSubject(v); reset(); }} options={[{ value: "", label: t("events.allSubjects") }, { value: "person", label: t("subject.person") }, { value: "vehicle", label: t("subject.vehicle") }]} />
      </div>

      <Panel bodyClassName="overflow-x-auto">
        <LoadState loading={eventsQ.isLoading} error={eventsQ.error} empty={!rows.length} emptyText={t("events.empty")} onRetry={() => eventsQ.refetch()}>
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-subtle text-left text-[11px] uppercase tracking-wide text-content-muted">
              <tr>
                <th className="px-3 py-2">{t("fields.when")}</th>
                <th className="px-3 py-2">{t("fields.capture")}</th>
                <th className="px-3 py-2">{t("fields.subject")}</th>
                <th className="px-3 py-2">{t("fields.point")}</th>
                <th className="px-3 py-2">{t("fields.method")}</th>
                <th className="px-3 py-2">{t("fields.direction")}</th>
                <th className="px-3 py-2">{t("fields.result")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {visible.map((e) => (
                <tr key={e.id} className="hover:bg-subtle">
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-content-secondary">{formatDateTime(e.occurred_at, i18n.language)}</td>
                  <td className="px-3 py-1.5">{e.snapshot_url ? <img src={e.snapshot_url} alt="" loading="lazy" className="h-9 w-16 rounded object-cover" /> : "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 text-content"><i className={`${e.subject_type === "vehicle" ? "ri-car-line" : "ri-user-line"} text-content-muted`} />{e.subject_name || t(`subject.${e.subject_type}`)}</div>
                    {e.plate && <span className="font-mono text-[11px] text-content-secondary">{e.plate}</span>}
                  </td>
                  <td className="px-3 py-2 text-content-secondary"><div className="text-content">{e.point_name ?? "—"}</div><div className="text-xs">{e.site_name}</div></td>
                  <td className="px-3 py-2 text-content-secondary"><i className={`${METHOD_ICON[e.method]} mr-1`} />{t(`method.${e.method}`)}</td>
                  <td className="px-3 py-2 text-content-secondary">{t(`direction.${e.direction ?? "entry"}`)}</td>
                  <td className="px-3 py-2">
                    <StatusChip tone={toneOf(RESULT_TONE, e.result)}>{t(`result.${e.result}`)}</StatusChip>
                    {e.reason && <div className="mt-0.5 text-[11px] text-content-muted">{e.reason}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-end gap-2 border-t border-line px-3 py-2 text-xs text-content-secondary">
            <button type="button" disabled={page <= 0} onClick={() => setPage((p) => p - 1)} className="rounded border border-line px-2 py-1 disabled:opacity-40">{t("common.prevPage")}</button>
            <span>{t("common.pageOf", { interpolation: { escapeValue: false }, page: page + 1, total: pages })}</span>
            <button type="button" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} className="rounded border border-line px-2 py-1 disabled:opacity-40">{t("common.nextPage")}</button>
          </div>
        </LoadState>
      </Panel>
    </ConsolePage>
  );
}
